// apps/web/src/features/arrival-cities/queries.ts
//
// Canonical React Query hook for the active arrival-city catalog.
// Three call sites share the `['arrival-cities-active']` key
// (`DestinationsField`, `RussiaRequestEditor`, `StartCityChips`); funnel
// them through one queryFn so the cached shape is invariant. Before
// this hook, `DestinationsField` wrote rows with `isDefault` / `active`
// while the Russia-side writers omitted those fields, and the
// China-side default-seeding effect could silently fall through to
// `allCities[0]` when the Russia cache landed first.

import { useQuery } from '@tanstack/react-query';
import { listArrivalCities } from '../../api/arrival-cities.js';

export interface ActiveArrivalCity {
  id: string;
  name: string;
  shortName: string;
  isDefault: boolean;
  active: boolean;
}

export const ARRIVAL_CITIES_ACTIVE_QK = ['arrival-cities-active'] as const;

export function useActiveArrivalCities(options: { enabled?: boolean } = {}) {
  return useQuery<ActiveArrivalCity[]>({
    queryKey: ARRIVAL_CITIES_ACTIVE_QK,
    queryFn: async () => {
      const res = await listArrivalCities({ active: true, limit: 200 });
      return res.items.map((c) => ({
        id: c.id,
        name: c.name,
        shortName: c.shortName,
        isDefault: c.isDefault,
        active: c.active,
      }));
    },
    enabled: options.enabled ?? true,
  });
}
